// The Admin area's tab bar (root 6.13): S12 Platform settings tabs plus the S10 Workflow editor. Linear: the
// connection and Sync now (poc CT-009). Scheduler: the worker's timed rules (M6), its own tab.
// Platform admins only; anyone else gets "You don't have access to this" (the API refuses them too).
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { ErrorState } from '@/components/ErrorState';
import { ApiError } from '@/lib/api';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';
import { PageTitle } from '../ui/primitives';

export const ADMIN_TABS = [
  { slug: 'security-groups', label: 'Security groups' },
  { slug: 'tool-settings', label: 'Tool settings' },
  { slug: 'lists', label: 'Lists' },
  { slug: 'ai-platforms', label: 'AI platforms' },
  { slug: 'mcp-clients', label: 'MCP clients' },
  { slug: 'linear', label: 'Linear' },
  { slug: 'workflow', label: 'Workflow' },
  { slug: 'data-requests', label: 'Data requests' },
  { slug: 'delivery-log', label: 'Delivery log' },
  { slug: 'scheduler', label: 'Scheduler' },
] as const;
export type AdminTabSlug = (typeof ADMIN_TABS)[number]['slug'];
export type PlatformTabSlug = Exclude<AdminTabSlug, 'workflow'>;

export function isPlatformTab(value: string): value is PlatformTabSlug {
  return value !== 'workflow' && ADMIN_TABS.some((t) => t.slug === value);
}

export function AdminLayout({ active, children }: { active: AdminTabSlug; children: ReactNode }) {
  const session = useSession();
  if (!session.isPlatformAdmin) {
    return (
      <ErrorState
        error={new ApiError(403, { code: 'forbidden', detail: 'The Admin pages are for Platform admins.' })}
      />
    );
  }
  const label = ADMIN_TABS.find((t) => t.slug === active)?.label ?? '';
  return (
    <div>
      <PageTitle crumbs={<span>Admin &gt; {label}</span>} title="Admin" />
      <nav aria-label="Admin sections" className="mb-4 border-b border-border">
        <ul className="-mb-px flex flex-wrap gap-x-1">
          {ADMIN_TABS.map((tab) => (
            <li key={tab.slug}>
              {tab.slug === 'workflow' ? (
                <Link
                  to="/admin/workflow"
                  aria-current={active === tab.slug ? 'page' : undefined}
                  className={tabClass(active === tab.slug)}
                >
                  {tab.label}
                </Link>
              ) : (
                <Link
                  to="/admin/$tab"
                  params={{ tab: tab.slug }}
                  aria-current={active === tab.slug ? 'page' : undefined}
                  className={tabClass(active === tab.slug)}
                >
                  {tab.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>
      {children}
    </div>
  );
}

function tabClass(current: boolean): string {
  return cn(
    'block border-b-2 px-3 py-2 text-sm font-medium',
    current ? 'border-primary text-text' : 'border-transparent text-muted hover:text-text',
  );
}
